/**
 * 将 HTML 元素转换为图片数据 URL 或下载为图片文件。
 *
 * 基于 `html-to-image`（浏览器引擎 + SVG foreignObject 渲染真实 DOM），
 * 支持复杂布局、外部字体、CSS 样式，无需 Canvas 手动绘制。
 *
 * ## 使用场景
 * - 导出海报、分享卡片、证书等需要截图的场景
 * - 生成自定义样式的图片（如水印、角标、装饰元素）
 *
 * ## 注意事项
 * - **动态导入**: `html-to-image` 库在使用时才加载，避免增加主 bundle 大小
 * - **跨域资源**: 外部图片需要 CORS 支持，否则会导致截图失败
 * - **性能**: 复杂 DOM 结构可能需要较长时间渲染，建议添加 loading 状态
 * - **字体加载**: 确保自定义字体已加载完成，否则可能出现字体回退
 *
 * @example
 * ```tsx
 * const handleSave = async () => {
 *   const element = document.getElementById('poster');
 *   await downloadElementAsImage(element, {
 *     filename: 'poster.png',
 *     pixelRatio: 2, // 2x 分辨率
 *   });
 * };
 * ```
 */

export interface ElementToPngOptions {
  /**
   * 像素比率（用于高清截图）
   * @default 2
   */
  pixelRatio?: number;

  /**
   * 背景色（透明背景传 'transparent'）
   * @default '#ffffff'
   */
  backgroundColor?: string;

  /**
   * 是否跳过自动缩放
   * @default false
   */
  skipAutoScale?: boolean;
}

export interface DownloadElementAsImageOptions extends ElementToPngOptions {
  /**
   * 下载的文件名
   */
  filename: string;
}

async function loadHtmlToImage() {
  const module = await import('html-to-image');
  return module.toPng;
}

/**
 * 将外部图片 URL 转换为 data URL，解决跨域问题
 */
async function imageUrlToDataUrl(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('无法获取 canvas context'));
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => reject(new Error(`图片加载失败: ${url}`));
    img.src = url;
  });
}

/**
 * 内联元素中的所有外部图片为 data URL
 */
async function inlineExternalImages(element: HTMLElement): Promise<Map<HTMLImageElement, string>> {
  const images = element.querySelectorAll('img');
  const originalSources = new Map<HTMLImageElement, string>();

  const inlinePromises = Array.from(images).map(async (img) => {
    const src = img.src;
    // 跳过已经是 data URL 的图片
    if (!src || src.startsWith('data:')) {
      return;
    }

    // 跳过同源图片（通常不会有跨域问题）
    try {
      const srcUrl = new URL(src, window.location.href);
      if (srcUrl.origin === window.location.origin) {
        return;
      }
    } catch {
      // URL 解析失败，跳过
      return;
    }

    // 保存原始 src
    originalSources.set(img, src);

    // 尝试转换为 data URL
    try {
      const dataUrl = await imageUrlToDataUrl(src);
      img.src = dataUrl;
    } catch (err) {
      console.warn(`图片内联失败 (${src}):`, err);
      // 失败时保持原 src，html-to-image 会尝试自己处理
    }
  });

  await Promise.all(inlinePromises);
  return originalSources;
}

/**
 * 恢复图片的原始 src
 */
function restoreImageSources(originalSources: Map<HTMLImageElement, string>): void {
  originalSources.forEach((src, img) => {
    img.src = src;
  });
}

export async function elementToPng(element: HTMLElement, options: ElementToPngOptions = {}): Promise<string> {
  const { pixelRatio = 3, backgroundColor, skipAutoScale = false } = options;

  // 等待所有字体加载完成，避免导出时字体回退
  if (document.fonts?.ready) {
    await document.fonts.ready;
  }

  // 内联外部图片为 data URL，解决跨域问题
  const originalSources = await inlineExternalImages(element);

  try {
    const toPng = await loadHtmlToImage();

    return await toPng(element, {
      pixelRatio,
      backgroundColor, // 不设置默认值，保持透明
      skipAutoScale,
      cacheBust: true, // 防止缓存导致的图片不一致
    });
  } finally {
    // 恢复原始图片 src
    restoreImageSources(originalSources);
  }
}

export async function downloadElementAsImage(
  element: HTMLElement,
  options: DownloadElementAsImageOptions,
): Promise<void> {
  const { filename, ...captureOptions } = options;
  const dataUrl = await elementToPng(element, captureOptions);

  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
